import {type SelectOptionItem, Button, Grid, List, Select} from '@yandex-delivery/blocks'
import {get} from '@yandex-delivery/utils'
import {useCallback, useMemo, useRef, useState} from 'react'
import {Controller, useFormContext} from 'react-hook-form'

import type {LocationDetectedVariant, PickupPoint} from '../../../api/address/model/types'
import {EventGoal, useFormUserEventHandler} from '../../../lib/metrika'
import {PointsByCityModal} from './PointsByCityModal/PointsByCityModal'
import {DestinationPointDeliveryMethod, SourcePointDeliveryMethod} from './PointsByCityModal/types'
import * as classes from './PointSelect.classes'
import type {PointSelectProps as Props} from './PointSelect.types'
import {PvzIcon} from './PvzIcon'

export function PointSelect(props: Props): React.ReactElement {
  const {
    name, //
    options = [],
    placeholder,
    trailButtonCaption,
    isPending,
    isSource,
  } = props
  const {control, setValue, watch} = useFormContext()
  const [opened, setOpened] = useState(false)

  const preparedOptions: SelectOptionItem<PickupPoint>[] = useMemo<SelectOptionItem<PickupPoint>[]>(() => {
    return options.map(option => ({
      id: option.pickupPoint._id,
      label: option.pickupPoint.name,
      value: option.pickupPoint,
    }))
  }, [options])

  const citySelectHandler = useCallback(
    (city?: LocationDetectedVariant) => {
      setValue(`${name}.city`, city)

      /**
       * @BusinessLogic
       * При смене города необходимо сбрасывать поле "Пункт приема"
       */
      setValue(`${name}.pickupPoint`, undefined, {shouldValidate: false})
    },
    [name, setValue],
  )

  const pointSelectHandler = useCallback(
    (point?: PickupPoint) => {
      setValue(`${name}.pickupPoint`, point)
    },
    [name, setValue],
  )

  const handleReachGoal = useFormUserEventHandler()

  //FIXME: реф решает проблему с задвоением клика в <Select<PickupPoint> />
  const lastClickTimeRef = useRef<number | null>(null)

  const handleClick = (event: React.MouseEvent) => {
    if (lastClickTimeRef.current === event.timeStamp) {
      return
    }

    lastClickTimeRef.current = event.timeStamp
    handleReachGoal(isSource ? EventGoal.AddressPickupFromSuggestClick : EventGoal.AddressPickupToSuggestClick)
  }

  return (
    <Controller
      control={control}
      name={`${name}.pickupPoint`}
      render={({field, fieldState}) => (
        <>
          <Select<PickupPoint>
            className={classes.select}
            disabled={options.length === 0}
            error={fieldState.invalid}
            filter={filter}
            helperText={fieldState.error?.message}
            isPending={isPending}
            makeKey={makeKey}
            name={`${name}.pickupPoint`}
            onChange={(item: SelectOptionItem<PickupPoint> | null) => {
              handleReachGoal(
                isSource
                  ? EventGoal.AddressPickupFromSuggestResultItemClick
                  : EventGoal.AddressPickupToSuggestResultItemClick,
              )
              field.onChange(item?.value)
            }}
            onClick={handleClick}
            options={preparedOptions}
            placeholder={placeholder}
            portal={false}
            renderOptionText={renderItem}
            trail={
              <Button
                className={classes.button}
                disabled={options.length === 0}
                loading={isPending}
                onPress={() => {
                  setOpened(true)
                }}
                shape="circle"
                size="xs"
                variant="outline"
              >
                {trailButtonCaption}
              </Button>
            }
            value={field.value}
            hideToggleIcon
          />
          {opened && (
            <PointsByCityModal
              isOpen={opened}
              isPending={false}
              onCitySelect={citySelectHandler}
              onClose={() => {
                setOpened(false)
              }}
              onPointSelect={pointSelectHandler}
              points={preparedOptions}
              pointType={isSource ? SourcePointDeliveryMethod.DropOffPoint : DestinationPointDeliveryMethod.PickUpPoint}
              selectedPointCity={watch(`${name}.city`)?.address}
            />
          )}
        </>
      )}
    />
  )
}

const makeKey: (o: SelectOptionItem<PickupPoint>) => string = get('value._id')

function renderItem(item: SelectOptionItem<PickupPoint>) {
  return (
    <Grid alignItems="center" size="s" wrap="nowrap" container>
      <Grid item>
        <PvzIcon />
      </Grid>
      <Grid size="s" container item xs>
        <Grid item xs>
          <List.Label variant="body2">
            <span className={classes.label}>{item.label}</span>
          </List.Label>
          <List.Description color="minor" variant="caption1">
            {item.value.name}
          </List.Description>
        </Grid>
      </Grid>
    </Grid>
  )
}

function filter(textValue: string, inputValue: string) {
  return textValue.toLowerCase().includes(inputValue.toLowerCase())
}
